import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const HEX = /^#[0-9A-Fa-f]{6}$/;

export const RealCoverPreview = ({ cat, theme }) => {
  const [titles, setTitles] = useState([]);
  const [pid, setPid] = useState("");
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const seq = useRef(0);

  useEffect(() => {
    api.get("/products").then((r) => setTitles(r.data.filter((p) => p.category_id === cat.id))).catch(() => {});
  }, [cat.id]);

  const key = `${theme.dark}${theme.accent}${theme.tint}${pid}`;
  useEffect(() => {
    if (![theme.dark, theme.accent, theme.tint].every((c) => HEX.test(c))) return;
    const n = ++seq.current;
    const t = setTimeout(async () => {
      setBusy(true);
      setErr("");
      try {
        const r = await api.post(`/categories/${cat.id}/cover-preview`, { theme: { ...theme, name: theme.name || "Custom" }, product_id: pid || null });
        if (n === seq.current) setImg(r.data);
      } catch (e) {
        if (n === seq.current) setErr(errMsg(e));
      } finally {
        if (n === seq.current) setBusy(false);
      }
    }, 900);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="border border-[#2A2325]/10 bg-[#FAF7F0] p-3 space-y-2" data-testid="real-cover-preview">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-[#5C5255]">Real PDF covers in these colours (not saved yet)</div>
        {titles.length > 1 && (
          <select value={pid} onChange={(e) => setPid(e.target.value)} className="h-7 border border-[#2A2325]/15 bg-white text-xs px-1 max-w-[220px]" data-testid="real-cover-title-select">
            <option value="">Most illustrated title</option>
            {titles.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
        )}
      </div>
      <div className="relative grid grid-cols-2 gap-3 min-h-[120px] max-w-sm">
        {img ? (
          <>
            <figure><img src={img.cover} alt="Front cover preview" className="w-full border border-[#2A2325]/10" data-testid="real-cover-front" /><figcaption className="text-[10px] text-[#5C5255] mt-1">Front cover</figcaption></figure>
            <figure><img src={img.back} alt="Back cover preview" className="w-full border border-[#2A2325]/10" data-testid="real-cover-back" /><figcaption className="text-[10px] text-[#5C5255] mt-1">Back cover with your logo</figcaption></figure>
          </>
        ) : !busy && <div className="col-span-2 text-xs italic text-[#5C5255] self-center text-center">{err || "Preparing preview…"}</div>}
        {busy && (
          <div className="absolute inset-0 bg-[#FAF7F0]/70 flex items-center justify-center gap-2 text-xs text-[#5C5255]" data-testid="real-cover-loading">
            <Loader2 size={14} className="animate-spin" /> Rendering the cover…
          </div>
        )}
      </div>
      {err && img && <div className="text-xs text-[#9B2C2C]" data-testid="real-cover-error">{err}</div>}
      {img && <div className="text-[10px] text-[#5C5255]" data-testid="real-cover-title">Showing “{img.title}”</div>}
    </div>
  );
};
